import { useCallback, useEffect, useEffectEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanModal } from '@/components';
import { EmailSubjectField } from '@/features/email-flows/components/EmailSubjectField';
import { EmailBodyField } from '@/features/email-flows/components/EmailBodyField';
import { useSendOptInEmail } from '../hooks/useSendOptInEmail';
import { useOptInRecipientCount } from '../queries';
import { type OptInEmailFormData, optInEmailFormSchema } from '../validations';

interface SendOptInEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (emailsSent: number) => void;
}

/**
 * Modal for sending opt-in emails to recipients who require opt-in
 * Allows customization of email subject and body
 */
export const SendOptInEmailModal = ({ isOpen, onClose, onSuccess }: SendOptInEmailModalProps) => {
    const { sendOptInEmail, isLoading, templateData } = useSendOptInEmail();

    const { data: recipientCount = 0 } = useOptInRecipientCount();

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors, isValid },
    } = useForm<OptInEmailFormData>({
        resolver: zodResolver(optInEmailFormSchema),
        defaultValues: {
            subject: '',
            body: '',
        },
    });

    const handleFormReset = useEffectEvent((template: typeof templateData) => {
        if (template && (template.subject || template.body)) {
            reset(
                {
                    subject: template.subject,
                    body: template.body,
                },
                { keepDirty: false }
            );
        }
    });

    // Reset form when template data becomes available (similar to useBroadcastForm)
    useEffect(() => {
        handleFormReset(templateData);
    }, [templateData]);

    const onSubmit = useCallback(
        async (data: OptInEmailFormData) => {
            // Prevent submission if no recipients
            if (recipientCount === 0) {
                return;
            }

            try {
                const result = await sendOptInEmail({
                    subject_template: data.subject,
                    body_template: data.body,
                });

                if (result) {
                    onSuccess(result.emails_triggered);
                    onClose();
                    // Reset form after successful send
                    reset(templateData || { subject: '', body: '' });
                }
            } catch (error) {
                // Error is already handled by the mutation's onError
                console.error('Failed to send opt-in emails:', error);
            }
        },
        [recipientCount, sendOptInEmail, onSuccess, onClose, reset, templateData]
    );

    const handleClose = useCallback(() => {
        onClose();
        // Reset form to template defaults when closing
        if (templateData) {
            reset(templateData);
        }
    }, [onClose, reset, templateData]);

    const hasRecipients = recipientCount > 0;

    return (
        <StanModal
            id='send-opt-in-email-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Send opt-in emails'
            size={hasRecipients ? 'lg' : 'md'}
            primaryActionLabel={hasRecipients ? 'Send' : 'Close'}
            secondaryActionLabel={hasRecipients ? 'Cancel' : undefined}
            onPrimaryAction={hasRecipients ? handleSubmit(onSubmit) : handleClose}
            onSecondaryAction={hasRecipients ? handleClose : undefined}
            primaryActionLoading={isLoading}
            primaryActionDisabled={hasRecipients && !isValid}
        >
            <div className='flex flex-col gap-6'>
                {!hasRecipients ? (
                    <div className='flex flex-col items-center gap-4 py-12'>
                        <p className='text-heading-h3 text-dark text-center'>No Recipients Require Opt-In</p>
                        <p className='text-caption-1-regular text-gray text-center'>
                            All your contacts are already subscribed or have been sent opt-in emails.
                        </p>
                    </div>
                ) : (
                    <>
                        {recipientCount > 0 && (
                            <p className='text-caption-1-regular text-gray -mt-2 text-center'>
                                You can send an email to {recipientCount} contact{recipientCount !== 1 ? 's' : ''} you
                                have imported so they can subscribe to your emails!
                            </p>
                        )}

                        <EmailSubjectField
                            control={control}
                            errors={errors}
                            templateVariables={templateData?.variables}
                            disabled={isLoading}
                        />

                        <EmailBodyField
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            templateVariables={templateData?.variables}
                            templateDefaults={templateData}
                            disabled={isLoading}
                        />
                    </>
                )}
            </div>
        </StanModal>
    );
};
